// SafetyRing renders a 0-100 score as a circular progress ring with a
// big number in the center and a tone-appropriate ring color. Pure SVG.

interface SafetyRingProps {
  score: number;
  size?: number;
  label?: string;
}

export function SafetyRing({ score, size = 140, label }: SafetyRingProps): JSX.Element {
  const clamped = Math.max(0, Math.min(100, score));
  const stroke = 10;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const dash = (clamped / 100) * circumference;
  const tone = clamped >= 85 ? 'good' : clamped >= 65 ? 'ok' : clamped >= 45 ? 'warn' : 'risk';
  const ringColor =
    tone === 'good'
      ? '#4f9d87'
      : tone === 'ok'
        ? '#5b8def'
        : tone === 'warn'
          ? '#c2410c'
          : '#b91c1c';
  return (
    <div
      className={`safety-ring safety-ring--${tone}`}
      style={{ width: size, height: size }}
      role="img"
      aria-label={`Safety score ${Math.round(clamped)} out of 100`}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <title>Safety score</title>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(255, 255, 255, 0.2)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={ringColor}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circumference - dash}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div className="safety-ring__center">
        <span className="safety-ring__score">{Math.round(clamped)}</span>
        {label ? <span className="safety-ring__label">{label}</span> : null}
      </div>
    </div>
  );
}
